import React, { useState, useCallback } from "react";
import { Outlet, useParams, useNavigate, useLocation } from "react-router-dom";
import Breadcrumb from "../../components/Breadcrumb";
import { Header, Switcher } from "../../components";
import {
  PlaygroundContainer,
  ViewPageWrapper,
  HeaderContainer,
  ContentWrapper,
  TestChatWrapper,
} from "./ViewPage.styled";
import { observer } from "mobx-react";
import { Alert } from "@ann/web-components";
import ChatComponentPersistent from "../../../PMSystemPage/components/ChatComponent/ChatComponentPersistent";
import { ChatProvider } from "../../../PMSystemPage/context/ChatContext";
import { useStore } from "../../../../../hooks";
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';

const ViewPage = observer(() => {
  const navigate = useNavigate();
  const { agentId } = useParams();
  const [agentName, setAgentName] = useState(`Agent ${agentId}`);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const { authStore, playgroundConfigStore } = useStore();


  // Configuration of the placeholder for the second chat (Test Agent)
  const testAgentPlaceholderConfig = {
    placeholderTitle: "Test your Agent",
    placeholderDescription: "Use this simulated chat to see how your agent responds\nSend a message to preview its behavior.",
    placeholderIcon: ChatBubbleOutlineRoundedIcon,
    showDefaultPlaceholder: true,
    chatStarters: playgroundConfigStore.agentStore.starters || []
  };

  //  HTTP Configuration
  const httpOptions = {
    tenantId: authStore.selectedAccount?.tenantId || "default-tenant",
    senderUuid: authStore.selectedAccount?.id,
    assistantUuid: agentId,
  };

  const saveImmediately = useCallback(async () => {
    setIsSaving(true);
    // Simulate save operation
    await new Promise((resolve) => setTimeout(resolve, 1000));
    setHasUnsavedChanges(false);
    setIsSaving(false);
  }, []);

  const handleClose = () => {
    setShowAlert(false);
  };

  const handleTabChange = useCallback(
    (tabName) => {
      // Navigate to the appropriate tab based on tabName
      if (tabName === "create") {
        navigate(`/v2/playground/${agentId}/create`);
      } else if (tabName === "configuration") {
        navigate(`/v2/playground/${agentId}/configuration`);
      } else if (tabName === "knowledge") {
        navigate(`/v2/playground/${agentId}/knowledge`);
      } else if (tabName === "instructions") {
        navigate(`/v2/playground/${agentId}/instructions`);
      } else if (tabName === "tools") {
        navigate(`/v2/playground/${agentId}/tools`);
      }
    },
    [navigate, agentId]
  );

  return (
    <PlaygroundContainer>
      <ViewPageWrapper>
        <HeaderContainer>
          <Breadcrumb
            agentName={agentName}
            hasUnsavedChanges={hasUnsavedChanges}
            saveImmediately={saveImmediately}
          />
          <Header
            agentName={agentName}
            agentId={agentId}
            hasUnsavedChanges={hasUnsavedChanges}
            isSaving={isSaving}
            saveImmediately={saveImmediately}
          />
          <Switcher onTabChange={handleTabChange} />
        </HeaderContainer>
        <ContentWrapper>
          <Outlet />
            <>
              <TestChatWrapper>
                <ChatProvider
                  communicationType="http"
                  httpOptions={httpOptions}
                >
                  <ChatComponentPersistent
                    customerSelected={authStore.selectedAccount?.id}
                    agentId={agentId}
                    agentDomain={"assistant"}
                    inputWidth="100%"
                    metadata={{}}
                    handleMetadata={() => {}}
                    agentData={{
                      id: agentId,
                      customerId: authStore.selectedAccount?.id,
                      jobTitle: "",
                      persona: { name: "" },
                      tone: [],
                      conversationStarters: [],
                      extendedAnswer: false,
                      scope: "",
                      actions: [],
                      actionsSettings: [],
                      name: agentName,
                      knowledgeBase: [],
                      humanInstructions: "",
                      prompt: "",
                      addToConcierge: false,
                      channels: [],
                      fullInstructions: "",
                      guardrails: [],
                      features: [],
                      instructionRef: [],
                      status: "active",
                      photoUrl: "",
                      model: "",
                      category: "",
                      signature: { EMAIL: "" },
                      type: "assistant",
                      promptId: "",
                      createdBy: "",
                    }}
                    {...testAgentPlaceholderConfig}
                  />
                </ChatProvider>
              </TestChatWrapper>
            </>

        </ContentWrapper>
      </ViewPageWrapper>
      {showAlert && (
        <Alert
          description="This section is under construction."
          severity="info"
          maxWidth="500px"
          closable={true}
          autoClose={false}
          autoCloseDelay={1000}
          onClose={handleClose}
        />
      )}
    </PlaygroundContainer>
  );
});

export default ViewPage;
